
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Trash2, AlertTriangle } from 'lucide-react';
import { Recipe, StructuredIngredient } from '@/types/recipe';
import { toast } from 'sonner';
import IngredientManager from './IngredientManager';
import { RecipeImageService } from '@/services/recipeImageService';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ImageUpload } from '@/components/ui/image-upload';

interface RecipeFormData {
  title: string;
  description: string;
  prepTime: string;
  cookTime: string;
  servings: string;
  sourceUrl: string;
  imageUrl: string;
  userImageUrl: string;
  userImagePath: string;
  instructions: string[];
}

interface RecipeFormProps {
  recipe?: Recipe;
  onSubmit: (data: Partial<Recipe>) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  isProcessingImage?: boolean;
}

const RecipeForm: React.FC<RecipeFormProps> = ({
  recipe,
  onSubmit,
  onCancel,
  isLoading = false,
  isProcessingImage = false
}) => {
  const [instructions, setInstructions] = useState<string[]>(
    recipe?.instructions || ['']
  );
  
  const [ingredients, setIngredients] = useState<StructuredIngredient[]>(() => {
    // Initialize with structured ingredients from the recipe
    const structuredIngredients = recipe?.structured_ingredients_metric || 
                                 recipe?.structured_ingredients_imperial ||
                                 recipe?.structured_ingredients;
    
    if (structuredIngredients && structuredIngredients.length > 0) {
      return structuredIngredients;
    }
    
    // Fallback to empty structured ingredient
    return [{ name: '', quantity: null, unit: '' }];
  });

  const [imageExplicitlyRemoved, setImageExplicitlyRemoved] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors }
  } = useForm<RecipeFormData>({
    defaultValues: {
      title: recipe?.title || '',
      description: recipe?.description || '',
      prepTime: recipe?.prepTime || '',
      cookTime: recipe?.cookTime || '',
      servings: recipe?.servings || '',
      sourceUrl: recipe?.source_url || '',
      imageUrl: recipe?.image_url || '',
      userImageUrl: recipe?.user_image_url || '',
      userImagePath: recipe?.user_image_path || '',
      instructions: recipe?.instructions || ['']
    }
  });

  const watchedTitle = watch('title');
  const watchedImageUrl = watch('imageUrl');

  useEffect(() => {
    setValue('instructions', instructions);
  }, [instructions, setValue]);

  // Check for image format warnings
  const imageFormatWarning = watchedImageUrl ? 
    RecipeImageService.getImageFormatWarning(watchedImageUrl) : null;

  const addInstruction = () => {
    setInstructions([...instructions, '']);
  };

  const removeInstruction = (index: number) => {
    if (instructions.length > 1) {
      setInstructions(instructions.filter((_, i) => i !== index));
    }
  };

  const updateInstruction = (index: number, value: string) => {
    const updated = [...instructions];
    updated[index] = value;
    setInstructions(updated);
  };

  const onFormSubmit = async (data: RecipeFormData) => {
    const validIngredients = ingredients.filter(ing => ing.name.trim());
    const validInstructions = instructions.filter(inst => inst.trim());

    if (validIngredients.length === 0) {
      toast.error('Please add at least one ingredient');
      return;
    }

    if (validInstructions.length === 0) {
      toast.error('Please add at least one instruction');
      return;
    }

    try {
      // Convert structured ingredients to simple strings for backward compatibility
      const ingredientStrings = validIngredients.map(ing => 
        `${ing.quantity || ''} ${ing.unit || ''} ${ing.name}`.trim()
      );

      await onSubmit({
        title: data.title,
        description: data.description || null,
        prepTime: data.prepTime || null,
        cookTime: data.cookTime || null,
        servings: data.servings || null,
        source_url: data.sourceUrl || null,
        user_image_url: data.userImageUrl || null,
        user_image_path: data.userImagePath || null,
        image_url: data.imageUrl || null,
        ingredients: ingredientStrings,
        structured_ingredients_metric: validIngredients,
        structured_ingredients_imperial: validIngredients,
        instructions: validInstructions
      });
    } catch (error) {
      toast.error('Failed to save recipe');
    }
  };

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardContent className="pt-6">
        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="title">Recipe Title *</Label>
              <Input
                id="title"
                {...register('title', { required: 'Title is required' })}
                placeholder="e.g., Spaghetti Carbonara"
                className="mt-1"
              />
              {errors.title && (
                <p className="text-sm text-red-500 mt-1">{errors.title.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="servings">Servings</Label>
              <Input
                id="servings"
                {...register('servings')}
                placeholder="4"
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              {...register('description')}
              placeholder="Brief description of the recipe..."
              className="mt-1"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="prepTime">Prep Time</Label>
              <Input
                id="prepTime"
                {...register('prepTime')}
                placeholder="15 minutes"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="cookTime">Cook Time</Label>
              <Input
                id="cookTime"
                {...register('cookTime')}
                placeholder="30 minutes"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="sourceUrl">Source URL</Label>
              <Input
                id="sourceUrl"
                {...register('sourceUrl')}
                placeholder="https://example.com/recipe"
                className="mt-1"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="imageUrl">Image URL (optional)</Label>
              <Input
                id="imageUrl"
                {...register('imageUrl')}
                placeholder="https://example.com/recipe-image.jpg"
                className="mt-1"
              />
              {imageFormatWarning && (
                <Alert className="mt-2">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription className="text-sm">
                    {imageFormatWarning}
                  </AlertDescription>
                </Alert>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Or Upload Image</Label>
              <ImageUpload
                recipeId={recipe?.id || 'temp'}
                currentImage={imageExplicitlyRemoved ? '' : (watch('userImageUrl') || watch('imageUrl'))}
                onUserImageUploaded={(url, path) => {
                  setValue('userImageUrl', url);
                  setValue('userImagePath', path || '');
                  setImageExplicitlyRemoved(url === '');
                }}
                onError={(error) => toast.error(error)}
                disabled={isLoading || isProcessingImage}
              />
            </div>
          </div>

          <IngredientManager 
            ingredients={ingredients}
            setIngredients={setIngredients}
          />

          <div>
            <Label className="text-base font-medium">Instructions *</Label>
            <div className="space-y-3 mt-2">
              {instructions.map((instruction, index) => (
                <div key={index} className="flex gap-2 items-start">
                  <div className="text-sm font-medium text-muted-foreground pt-3 min-w-6">
                    {index + 1}.
                  </div>
                   <Textarea
                     placeholder="Add instruction step..."
                     value={instruction}
                     onChange={(e) => updateInstruction(index, e.target.value)}
                     className="flex-1"
                   />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removeInstruction(index)}
                    disabled={instructions.length === 1}
                    className="mt-2"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={addInstruction}
                className="w-full"
              >
                <Plus size={16} className="mr-2" />
                Add Instruction
              </Button>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              type="submit"
              disabled={isLoading || isProcessingImage || !watchedTitle.trim()}
              className="flex-1"
            >
              {isProcessingImage ? 'Processing Image...' : 
               isLoading ? 'Saving...' : 
               recipe ? 'Update Recipe' : 'Create Recipe'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isLoading || isProcessingImage}
            >
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default RecipeForm;
